import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Clock, User, MapPin, Phone, FileText, ExternalLink, ClipboardList, StickyNote } from 'lucide-react';
import { getTenantTimezone, parseUTC } from '../lib/timezone';

const STATUS_LABELS = {
  scheduled: { label: 'Scheduled', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  en_route: { label: 'En Route', cls: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
  on_site: { label: 'On Site', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  done: { label: 'Done', cls: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
};

function formatCardTime(dt) {
  if (!dt) return '';
  const tz = getTenantTimezone();
  return new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true }).format(parseUTC(dt));
}

function formatCardDate(dt) {
  if (!dt) return '';
  const tz = getTenantTimezone();
  return new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric' }).format(parseUTC(dt));
}

function HoverCardBody({ evt, showPrivateNotes }) {
  const navigate = useNavigate();
  const st = STATUS_LABELS[evt.status] || STATUS_LABELS.scheduled;
  const hasEstimate = !!evt.estimate_id;

  return (
    <div className="w-64 rounded-lg border bg-popover text-popover-foreground shadow-lg p-3 space-y-2" data-testid={`hover-card-${evt.id}`}>
      {/* Header: Type badge + status */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" data-testid={`hover-type-${evt.id}`}>
            <Briefcase className="w-3 h-3" /> Job
          </span>
          {hasEstimate && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400" data-testid={`hover-estimate-badge-${evt.id}`}>
              <ClipboardList className="w-3 h-3" /> Estimate #{evt.estimate_number}
            </span>
          )}
        </div>
        <span className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${st.cls}`}>{st.label}</span>
      </div>

      {/* Job number + name */}
      <div>
        <span className="font-semibold text-sm">#{evt.job_number}</span>
        {evt.job_name && <span className="text-sm text-muted-foreground ml-1.5">— {evt.job_name}</span>}
      </div>

      {/* Time */}
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Clock className="w-3.5 h-3.5 shrink-0" />
        <span>{formatCardDate(evt.start_at)} {formatCardTime(evt.start_at)} — {formatCardTime(evt.end_at)}</span>
      </div>

      {/* Customer */}
      {evt.customer_name && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <User className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate">{evt.customer_name}</span>
        </div>
      )}

      {/* Address */}
      {evt.customer_address && (
        <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span className="break-words">{evt.customer_address}</span>
        </div>
      )}

      {/* Technician */}
      {evt.assigned_user_name && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <User className="w-3.5 h-3.5 shrink-0 text-primary/60" />
          <span className="truncate">{evt.assigned_user_name}</span>
        </div>
      )}

      {/* Phone */}
      {evt.customer_phone && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Phone className="w-3.5 h-3.5 shrink-0" />
          <span>{evt.customer_phone}</span>
        </div>
      )}

      {/* Dispatch Notes */}
      {evt.notes && (
        <div className="flex items-start gap-1.5 text-xs text-muted-foreground border-t pt-1.5">
          <FileText className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span className="line-clamp-2">{evt.notes}</span>
        </div>
      )}

      {/* Private Job Notes (only when toggle is on) */}
      {showPrivateNotes && evt.job_notes && (
        <div className="flex items-start gap-1.5 text-xs text-muted-foreground border-t pt-1.5" data-testid={`hover-private-notes-${evt.id}`}>
          <StickyNote className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-500" />
          <div className="min-w-0">
            <span className="text-[10px] font-medium text-amber-600 dark:text-amber-400">Private Notes</span>
            <p className="line-clamp-4 mt-0.5">{evt.job_notes}</p>
          </div>
        </div>
      )}

      {/* Action links */}
      <div className="flex items-center gap-2 pt-1 border-t">
        <button
          className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          onClick={(e) => { e.stopPropagation(); navigate(`/jobs/${evt.job_id}`); }}
          data-testid={`hover-view-job-${evt.id}`}
        >
          <ExternalLink className="w-3 h-3" /> View Job
        </button>
        {hasEstimate && (
          <button
            className="flex items-center gap-1 text-xs font-medium text-purple-600 hover:underline"
            onClick={(e) => { e.stopPropagation(); navigate(`/estimates/${evt.estimate_id}`); }}
            data-testid={`hover-view-estimate-${evt.id}`}
          >
            <ExternalLink className="w-3 h-3" /> View Estimate
          </button>
        )}
      </div>
    </div>
  );
}

export function EventHoverCard({ evt, showPrivateNotes, children }) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const enterTimer = useRef(null);
  const leaveTimer = useRef(null);
  const wrapRef = useRef(null);
  const cardRef = useRef(null);

  const show = useCallback((e) => {
    // Don't show during drag
    if (e.buttons > 0) return;
    clearTimeout(leaveTimer.current);
    enterTimer.current = setTimeout(() => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      const cardW = 272; // w-64 + padding
      const cardH = 220; // estimate

      // Prefer right side, fall back to left
      let x = rect.right + 8;
      if (x + cardW > window.innerWidth - 16) {
        x = rect.left - cardW - 8;
      }
      if (x < 8) x = 8;

      // Center vertically on the event, clamp to viewport
      let y = rect.top + rect.height / 2 - cardH / 2;
      y = Math.max(8, Math.min(y, window.innerHeight - cardH - 8));

      setPos({ x, y });
      setVisible(true);
    }, 200);
  }, []);

  const hide = useCallback(() => {
    clearTimeout(enterTimer.current);
    leaveTimer.current = setTimeout(() => setVisible(false), 100);
  }, []);

  const keepAlive = useCallback(() => {
    clearTimeout(leaveTimer.current);
  }, []);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onDragStart = () => {
      clearTimeout(enterTimer.current);
      setVisible(false);
    };
    el.addEventListener('dragstart', onDragStart, true);
    return () => {
      el.removeEventListener('dragstart', onDragStart, true);
      clearTimeout(enterTimer.current);
      clearTimeout(leaveTimer.current);
    };
  }, []);

  return (
    <>
      <div
        ref={wrapRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        className="h-full"
      >
        {children}
      </div>
      {visible && createPortal(
        <div
          ref={cardRef}
          onMouseEnter={keepAlive}
          onMouseLeave={hide}
          className="fixed z-[9999] pointer-events-auto animate-in fade-in-0 zoom-in-95 duration-150"
          style={{ left: pos.x, top: pos.y }}
          data-testid="event-hover-card"
        >
          <HoverCardBody evt={evt} showPrivateNotes={showPrivateNotes} />
        </div>,
        document.body
      )}
    </>
  );
}
